/**
 * IgnoredModal — v0.8.6 (Bill 2026-05-15).
 *
 * Consolidated "Ignored" surface invoked from a header button in
 * IdentityShell. Three categories — Field ignores / Group ignores /
 * Subordinate overrides — each rendered as a sub-tab.
 *
 * Per Bill 2026-05-15 UI consolidation:
 *   - Modal title: "Ignored" (not "Sync ignores + overrides")
 *   - Sub-tab is hidden entirely when its list is empty
 *   - Sub-tab label carries a count badge (e.g., "Field (3)")
 *   - Row action labelled "Restore" with the rotate-counterclockwise icon
 *     for visual coherence across the three categories
 *
 * Replaces the SyncTab-embedded IgnoresModal (deleted in this commit).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { RotateCcw } from '@retail-ops/panel-shell';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Pill, Spinner } from '@/components/primitives';
import { ModalShell } from '@/components/ModalShell';
import type {
  FieldIgnoreRow,
  GroupIgnoreRowV08,
  SubordinateOverrideRow,
} from '@retail-ops/api-client';

type SubTab = 'field' | 'group' | 'subordinate';

export function IgnoredModal({ open, onClose }: { open: boolean; onClose: () => void }): JSX.Element {
  const client = useApiClient();
  const [tab, setTab] = useState<SubTab | null>(null);
  const [fieldRows, setFieldRows] = useState<ReadonlyArray<FieldIgnoreRow> | null>(null);
  const [groupRows, setGroupRows] = useState<ReadonlyArray<GroupIgnoreRowV08> | null>(null);
  const [subRows, setSubRows] = useState<ReadonlyArray<SubordinateOverrideRow> | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // v0.8.9 — resolve raw GUIDs (entraObjectId, groupId) on Group ignores
  // to human-readable user + role labels. Without this the table is a
  // wall of truncated GUIDs and nobody can tell what they're restoring.
  const [oidToUser, setOidToUser] = useState<Map<string, { displayName: string; email: string }>>(new Map());
  const [groupIdToRole, setGroupIdToRole] = useState<Map<string, { code: string; name: string }>>(new Map());

  const reloadAll = useCallback(async () => {
    setErr(null); setLoading(true);
    try {
      const [f, g, s, usersResp, rolesResp] = await Promise.all([
        client.retail.admin.listFieldSyncIgnores(),
        client.retail.admin.listGroupSyncIgnores(),
        client.retail.admin.listSubordinateOverrides(),
        client.retail.config.users.list(),
        client.retail.config.roles(),
      ]);
      if (f.success) setFieldRows(f.data);
      if (g.success) setGroupRows(g.data);
      if (s.success) setSubRows(s.data);

      // Build oid -> user lookup. The user-roles envelope has either
      // {users: [...]} or a bare array depending on the deployment.
      const usersData = usersResp.success ? (usersResp.data as unknown) : null;
      const usersArr =
        usersData && typeof usersData === 'object' && !Array.isArray(usersData) && 'users' in usersData
          ? (usersData as { users: Array<Record<string, unknown>> }).users
          : Array.isArray(usersData)
            ? (usersData as Array<Record<string, unknown>>)
            : [];
      const oidMap = new Map<string, { displayName: string; email: string }>();
      for (const u of usersArr) {
        const oid = (u.entraObjectId as string | null) ?? null;
        if (!oid) continue;
        oidMap.set(oid.toLowerCase(), {
          displayName: (u.displayName as string) ?? '',
          email: (u.email as string) ?? '',
        });
      }
      setOidToUser(oidMap);

      // Build groupId -> role lookup.
      const rolesArr = rolesResp.success && Array.isArray(rolesResp.data)
        ? (rolesResp.data as Array<{ code?: string; name?: string; groupId?: string | null }>)
        : [];
      const groupMap = new Map<string, { code: string; name: string }>();
      for (const r of rolesArr) {
        if (!r.groupId || !r.code) continue;
        groupMap.set(r.groupId.toLowerCase(), { code: r.code, name: r.name ?? r.code });
      }
      setGroupIdToRole(groupMap);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [client]);

  useEffect(() => {
    if (!open) return;
    void reloadAll();
  }, [open, reloadAll]);

  // Visible tabs (non-empty only) + select the first one when the modal opens.
  const visibleTabs = useMemo<ReadonlyArray<{ key: SubTab; label: string; count: number }>>(() => {
    const out: { key: SubTab; label: string; count: number }[] = [];
    if (fieldRows && fieldRows.length > 0) out.push({ key: 'field', label: 'Field', count: fieldRows.length });
    if (groupRows && groupRows.length > 0) out.push({ key: 'group', label: 'Group', count: groupRows.length });
    if (subRows && subRows.length > 0) out.push({ key: 'subordinate', label: 'Subordinate', count: subRows.length });
    return out;
  }, [fieldRows, groupRows, subRows]);

  useEffect(() => {
    if (visibleTabs.length === 0) { setTab(null); return; }
    if (tab === null || !visibleTabs.some((t) => t.key === tab)) {
      setTab(visibleTabs[0].key);
    }
  }, [visibleTabs, tab]);

  const restoreField = useCallback(async (id: number) => {
    await client.retail.admin.deleteFieldSyncIgnore(id);
    await reloadAll();
  }, [client, reloadAll]);
  const restoreGroup = useCallback(async (id: string) => {
    await client.retail.admin.deleteGroupSyncIgnore(id);
    await reloadAll();
  }, [client, reloadAll]);
  const restoreSubordinate = useCallback(async (id: number) => {
    await client.retail.admin.deleteSubordinateOverride(id);
    await reloadAll();
  }, [client, reloadAll]);

  const totalCount = (fieldRows?.length ?? 0) + (groupRows?.length ?? 0) + (subRows?.length ?? 0);

  return (
    <ModalShell open={open} title={`Ignored${totalCount > 0 ? ` (${totalCount})` : ''}`} onClose={onClose} width={840}>
      {err && <Banner tone="err">{err}</Banner>}
      {loading && (fieldRows === null || groupRows === null || subRows === null) && (
        <Spinner label="Loading ignore lists…" />
      )}
      {!loading && visibleTabs.length === 0 && (
        <p className="form-hint">No ignored rows. Items appear here when you ignore them from a Sync result.</p>
      )}
      {visibleTabs.length > 0 && (
        <>
          <div className="sync-mode-tabs">
            {visibleTabs.map((t) => (
              <button
                key={t.key}
                type="button"
                className={tab === t.key ? 'sync-mode-tab sync-mode-tab--active' : 'sync-mode-tab'}
                onClick={() => setTab(t.key)}
              >
                {t.label} ({t.count})
              </button>
            ))}
          </div>
          {tab === 'field' && fieldRows && <FieldIgnoresTable rows={fieldRows} onRestore={restoreField} />}
          {tab === 'group' && groupRows && <GroupIgnoresTable rows={groupRows} onRestore={restoreGroup} oidToUser={oidToUser} groupIdToRole={groupIdToRole} />}
          {tab === 'subordinate' && subRows && <SubordinateOverridesTable rows={subRows} onRestore={restoreSubordinate} />}
        </>
      )}
    </ModalShell>
  );
}

// ── Table renderers ────────────────────────────────────────────────────

function FieldIgnoresTable({ rows, onRestore }: { rows: ReadonlyArray<FieldIgnoreRow>; onRestore: (id: number) => void | Promise<void> }) {
  return (
    <table className="data-table">
      <thead><tr><th>User</th><th>Field</th><th>Reason</th><th>Since</th><th><span className="sr-only">Action</span></th></tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.ignoreId}>
            <td><code className="cell-mono">{r.email}</code></td>
            <td><code className="cell-mono">{r.fieldName}</code></td>
            <td>{r.reason ?? <span className="cell-dim">—</span>}</td>
            <td className="cell-mono">{new Date(r.ignoredAt).toLocaleString()}</td>
            <td className="ta-right">
              <Button variant="ghost" size="sm" onClick={() => void onRestore(r.ignoreId)}>
                <RotateCcw size={14} /> Restore
              </Button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function GroupIgnoresTable({
  rows, onRestore, oidToUser, groupIdToRole,
}: {
  rows: ReadonlyArray<GroupIgnoreRowV08>;
  onRestore: (id: string) => void | Promise<void>;
  oidToUser: Map<string, { displayName: string; email: string }>;
  groupIdToRole: Map<string, { code: string; name: string }>;
}) {
  return (
    <table className="data-table">
      <thead><tr><th>User</th><th>Role / Group</th><th>Reason</th><th>Since</th><th><span className="sr-only">Action</span></th></tr></thead>
      <tbody>
        {rows.map((r) => {
          const user = oidToUser.get(r.entraObjectId.toLowerCase());
          const role = groupIdToRole.get(r.groupId.toLowerCase());
          return (
            <tr key={r.ignoreId}>
              <td>
                {user ? (
                  <>
                    <strong>{user.displayName}</strong>
                    <br />
                    <code className="cell-mono">{user.email}</code>
                  </>
                ) : (
                  <>
                    <span className="cell-dim">Unknown user</span>
                    <br />
                    <code className="cell-mono">{r.entraObjectId.slice(0, 8)}…</code>
                  </>
                )}
              </td>
              <td>
                {role ? (
                  <>
                    <strong>{role.name}</strong>
                    <br />
                    <code className="cell-mono">{role.code}</code>
                  </>
                ) : (
                  <>
                    <span className="cell-dim">Unknown group</span>
                    <br />
                    <code className="cell-mono">{r.groupId.slice(0, 8)}…</code>
                  </>
                )}
              </td>
              <td>{r.reason ?? <span className="cell-dim">—</span>}</td>
              <td className="cell-mono">{new Date(r.ignoredAt).toLocaleString()}</td>
              <td className="ta-right">
                <Button variant="ghost" size="sm" onClick={() => void onRestore(r.ignoreId)}>
                  <RotateCcw size={14} /> Restore
                </Button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function SubordinateOverridesTable({ rows, onRestore }: { rows: ReadonlyArray<SubordinateOverrideRow>; onRestore: (id: number) => void | Promise<void> }) {
  return (
    <table className="data-table">
      <thead><tr><th>Supervisor</th><th>Subordinate</th><th>Kind</th><th>Reason</th><th>Since</th><th><span className="sr-only">Action</span></th></tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.overrideId}>
            <td><code className="cell-mono">{r.supervisorEmail}</code></td>
            <td><code className="cell-mono">{r.subordinateEmail}</code></td>
            <td><Pill tone={r.overrideKind === 'add' ? 'ok' : 'warn'}>{r.overrideKind}</Pill></td>
            <td>{r.reason ?? <span className="cell-dim">—</span>}</td>
            <td className="cell-mono">{new Date(r.createdAt).toLocaleString()}</td>
            <td className="ta-right">
              <Button variant="ghost" size="sm" onClick={() => void onRestore(r.overrideId)}>
                <RotateCcw size={14} /> Restore
              </Button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
